// Split payment - one payment at a time (owner, Oct 29 2026: "as simple as the main app").
//
//   Split payment -> choose how the customer pays -> the amount (prefilled with
//   what remains; Half / All) -> Charge / Take cash -> the next payment ... until
//   REMAINING is $0.00 -> paid -> receipt.
//
// "Even split" and "By item" only PREFILL the amount (never a step of their own).
// The same body runs on the register (dark dialog) and on a dining check (light
// modal); `adapter` is the only difference (payments/split_tender.py "next" mode
// behind both). Nothing here decides money: a payment is paid only when the
// server read Aster Gateway's verdict; REMAINING is the server's number. A
// declined card charged nothing - try again or choose another method; a partial
// approval leaves the rest in REMAINING; no answer = "Check again" with the same
// request (never a second charge).
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { C, FONT_TIGHT } from '../theme/tokens';
import { IconCard, IconCash, IconCheck, IconMinus, IconPlus, IconRefresh, IconSpinner, IconSplit } from './icons';

const POLL_MS = 2000;
const EVEN_MIN = 2;
const EVEN_MAX = 10;

const usd = (c) => `${(c || 0) < 0 ? '-' : ''}$${(Math.abs(c || 0) / 100).toFixed(2)}`;
const toCents = (text) => Math.round((Number(String(text ?? '').replace(/[^0-9.]/g, '')) || 0) * 100);
const asText = (c) => ((c || 0) / 100).toFixed(2);

export const TENDER_LABEL = { card: 'Card', cash: 'Cash', zelle: 'Zelle', venmo: 'Venmo', crypto: 'Crypto', gift_card: 'Gift card', loyalty: 'Loyalty' };

/** Even split: part k of n of what remains now (floor; the last part takes the rest). Pure. */
export function evenAmount(dueCents, ways, done) {
  const left = Math.max(ways - done, 1);
  if (!(dueCents > 0)) return 0;
  return left <= 1 ? dueCents : Math.floor(dueCents / left);
}

/** By item: the chosen items' share of the bill before tip (their tax / discount / service share
 *  in proportion); every item left = exactly what remains. Pure. */
export function itemsAmount(view, chosen) {
  const lines = view?.lines || [];
  const open = lines.filter((l) => !view.paidItems.has(l.id));
  if (!chosen.size || !open.length) return 0;
  if (open.every((l) => chosen.has(l.id))) return view.due;
  const all = lines.reduce((n, l) => n + l.amount, 0);
  const sel = lines.filter((l) => chosen.has(l.id)).reduce((n, l) => n + l.amount, 0);
  if (!(all > 0)) return 0;
  return Math.min(Math.round((sel * view.base) / all), view.due);
}

function palette(dark) {
  return dark
    ? {
      text: '#F1F5F9', muted: '#94A3B8', faint: '#64748B', border: '#334155', card: '#1E293B', input: '#0F172A',
      panel: 'rgba(59,130,246,.12)', panelInk: '#93C5FD', big: '#FFFFFF', accent: '#2563EB', accentFg: '#FFFFFF',
      chipOn: '#F1F5F9', chipOnFg: '#0F172A', ok: '#4ADE80', okBg: 'rgba(34,197,94,.12)', warnBg: 'rgba(245,158,11,.12)',
      warn: '#FCD34D', badBg: 'rgba(239,68,68,.12)', bad: '#FCA5A5', link: '#CBD5E1', danger: '#FCA5A5',
    }
    : {
      text: C.ink, muted: C.muted, faint: C.faint, border: C.border, card: '#FFFFFF', input: '#FAF8F5',
      panel: C.orangeTint, panelInk: C.orangeInk, big: C.ink, accent: C.orange, accentFg: C.orangeDeep,
      chipOn: C.ink, chipOnFg: '#FFFFFF', ok: C.greenInk, okBg: C.greenSoft, warnBg: C.amberSoft, warn: C.amberInk,
      badBg: C.redSoft, bad: C.redInk, link: C.ink3, danger: C.redInk,
    };
}

const TILE = {
  card: { dark: '#2563EB', icon: IconCard },
  cash: { dark: '#16A34A', icon: IconCash },
  zelle: { dark: '#7C3AED' },
  venmo: { dark: '#0284C7' },
  crypto: { dark: '#EA580C' },
};

/**
 * adapter: {
 *   load() -> view, next(body) -> view, card(n) -> { view, payment }, poll(paymentId) -> { view, payment },
 *   cash(n, tenderedCents) -> { view, change }, manual(n, method) -> view, cancelCard(paymentId), cancelSplit() -> view
 * }
 * view (normalized): { due, base, paid, payments[{ key, label, amount, tip, fee, voided }], lines[{ id, name, qty, amount }],
 *   paidItems: Set, split, waitingPaymentId, unknownPart, complete }
 * tenders: the ways this restaurant takes money, e.g. [{ id: 'card' }, { id: 'cash' }] - only what is set up.
 */
const SplitPayFlow = forwardRef(function SplitPayFlow({
  adapter, tenders, tips = {}, dark = false, cardReady = true, cardHint, terminalLabel,
  onPaid, onLeave, onClose, onWaitingCard, notify,
}, ref) {
  const P = palette(dark);
  const [view, setView] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [tender, setTender] = useState(null);
  const [amountText, setAmountText] = useState('');
  const [tipPct, setTipPct] = useState(0);
  const [tendered, setTendered] = useState('');
  const [helper, setHelper] = useState('custom');
  const [ways, setWays] = useState(2);
  const [evenFrom, setEvenFrom] = useState(0);
  const [chosen, setChosen] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(null); // { paymentId, amount }
  const [note, setNote] = useState(null); // { tone: ok | warn | bad, text }
  const alive = useRef(true);
  const pollTimer = useRef(null);
  const doneRef = useRef(false);
  const inFlight = useRef(false);

  const say = useCallback((tone, text) => { setNote({ tone, text }); notify?.(tone, text); }, [notify]);

  const taken = view ? view.payments.filter((p) => !p.voided).length : 0;
  const firstPayment = taken === 0;

  const apply = useCallback((v) => {
    if (!alive.current || !v) return v;
    setView(v);
    setLoadError('');
    return v;
  }, []);

  const load = useCallback(async () => {
    try {
      return apply(await adapter.load());
    } catch (e) {
      if (alive.current) setLoadError(e.message || 'The bill could not be read');
      return null;
    }
  }, [adapter, apply]);

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; clearTimeout(pollTimer.current); };
  }, [load]);

  // The amount box: prefilled with what remains; the helpers fill it their way.
  const prefill = useCallback((v, h = helper) => {
    if (!v) return;
    if (h === 'even') setAmountText(asText(evenAmount(v.due, ways, v.payments.filter((p) => !p.voided).length - evenFrom)));
    else if (h === 'item' && chosen.size) setAmountText(asText(itemsAmount(v, chosen)));
    else setAmountText(asText(v.due));
  }, [helper, ways, evenFrom, chosen]);

  useEffect(() => { prefill(view); }, [view?.due, helper, ways, chosen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Paid in full: the server's word (the order / check is paid).
  useEffect(() => {
    if (view?.complete && !doneRef.current) {
      doneRef.current = true;
      onPaid?.(view);
    }
  }, [view, onPaid]);

  // A card already on the terminal (reopened, or another register's): wait for it here.
  useEffect(() => {
    if (view?.waitingPaymentId && !waiting) setWaiting({ paymentId: view.waitingPaymentId, amount: null });
  }, [view?.waitingPaymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishCard = useCallback((payment, v) => {
    const st = String(payment?.status || '');
    if (st === 'approved') {
      const got = payment.approved_cents ?? payment.amount_cents;
      if (got != null && got < payment.amount_cents) {
        say('warn', `The card approved ${usd(got)} of ${usd(payment.amount_cents)} - ${usd(v?.due)} still remaining. Take the next payment.`);
      } else {
        say('ok', `Card${payment.last4 ? ` •••• ${payment.last4}` : ''} approved · ${usd((payment.approved_cents ?? payment.amount_cents) + (payment.tip_cents || 0))}`);
      }
      setTender(null);
      setTipPct(0);
      setChosen(new Set());
    } else if (st === 'declined') {
      say('bad', 'Card declined - nothing was charged. Try the card again or choose another method.');
    } else if (st === 'cancelled') {
      say('bad', 'Card payment cancelled - nothing was charged.');
    } else {
      say('bad', payment?.message || 'The terminal did not answer. Check again - the card is never charged twice.');
    }
  }, [say]);

  // Waiting on the terminal: ask the server (which asks Aster Gateway) every 2 s.
  useEffect(() => {
    clearTimeout(pollTimer.current);
    if (!waiting) { onWaitingCard?.(false); return undefined; }
    onWaitingCard?.(true);
    const tick = async () => {
      try {
        const { view: v, payment } = await adapter.poll(waiting.paymentId);
        if (!alive.current) return;
        if (v) apply(v);
        if (payment && payment.is_final) {
          setWaiting(null);
          const fresh = v || await load();
          finishCard(payment, fresh);
          return;
        }
      } catch (e) {
        // a dropped connection never decides a payment: keep asking
      }
      if (alive.current) pollTimer.current = setTimeout(tick, POLL_MS);
    };
    pollTimer.current = setTimeout(tick, POLL_MS);
    return () => clearTimeout(pollTimer.current);
  }, [waiting]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    /** The dialog is closing: true = this body answers it instead (a card waiting, or money taken). */
    requestClose() {
      if (waiting) { say('bad', 'A card is waiting on the terminal - cancel it there first.'); return true; }
      if (view && taken > 0 && view.due > 0) { onLeave?.(view); return true; }
      if (view?.split && taken === 0) adapter.cancelSplit().catch(() => {});
      return false;
    },
  }), [waiting, view, taken, adapter, onLeave, say]);

  const amount = toCents(amountText);
  const tipOn = tender === 'card' && tips.enabled && !tips.onTerminal;
  const tip = tipOn ? Math.round((amount * tipPct) / 100) : 0;
  const cashIn = tendered ? toCents(tendered) : null;
  const unknown = view?.unknownPart || null;
  const problem = !view ? null
    : !(amount > 0) ? 'Enter an amount'
      : amount > view.due ? `That is more than the ${usd(view.due)} remaining`
        : tender === 'cash' && cashIn !== null && cashIn < amount ? 'Cash received is less than the amount'
          : tender === 'card' && !cardReady ? (cardHint || 'Choose this register’s card terminal first') : null;

  const run = async (fn) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNote(null);
    try {
      await fn();
    } catch (e) {
      say('bad', e.message || 'The payment did not go through');
      load();
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const process = () => run(async () => {
    if (!tender || problem) return;
    const body = { mode: 'next', amount_cents: amount, tip_cents: tip };
    if (helper === 'item' && chosen.size) body.items = [...chosen];
    const planned = apply(await adapter.next(body));
    const n = planned?.split?.next_part;
    if (!n) throw new Error('The payment could not be set up - try again');
    if (tender === 'card') {
      const { view: v, payment } = await adapter.card(n);
      if (v) apply(v);
      if (payment?.warning) say('warn', payment.warning);
      if (payment && !payment.is_final) setWaiting({ paymentId: payment.payment_id, amount });
      else finishCard(payment, v || await load());
    } else if (tender === 'cash') {
      const { view: v, change } = await adapter.cash(n, cashIn ?? amount);
      apply(v);
      say('ok', change > 0 ? `Cash ${usd(amount)} taken · give ${usd(change)} change` : `Cash ${usd(amount)} taken`);
      setTender(null);
      setTendered('');
      setChosen(new Set());
    } else {
      apply(await adapter.manual(n, tender));
      say('ok', `${TENDER_LABEL[tender] || tender} ${usd(amount)} recorded`);
      setTender(null);
      setChosen(new Set());
    }
  });

  const checkAgain = () => run(async () => {
    const { view: v, payment } = await adapter.card(unknown.n);
    if (v) apply(v);
    if (payment && !payment.is_final) setWaiting({ paymentId: payment.payment_id, amount: unknown.amount });
    else finishCard(payment, v || await load());
  });

  const cancelCard = () => run(async () => {
    await adapter.cancelCard(waiting.paymentId);
    say('warn', 'Cancel sent to the terminal - waiting for its answer');
  });

  const cancelSplit = () => {
    if (busy || waiting) return;
    if (view && taken > 0 && view.due > 0) { onLeave?.(view); return; }
    run(async () => {
      if (view?.split) await adapter.cancelSplit();
      onClose?.();
    });
  };

  const pickHelper = (h) => {
    setHelper(h);
    if (h === 'even') setEvenFrom(taken);
    if (h !== 'item') setChosen(new Set());
  };

  const toggleItem = (id) => setChosen((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const evenPart = Math.min(taken - evenFrom + 1, ways);
  const tenderList = useMemo(() => (tenders || []).filter(Boolean), [tenders]);

  if (!view) {
    return (
      <div style={{ padding: '36px 0', textAlign: 'center', color: P.muted }} data-testid="split-loading">
        {loadError ? <span style={{ color: P.bad, fontSize: 14 }}>{loadError}</span> : <IconSpinner size={28} />}
      </div>
    );
  }

  const tileStyle = (id, on, disabled) => {
    const t = TILE[id] || {};
    if (dark) {
      return {
        height: 64, borderRadius: 14, border: on ? '2px solid #F8FAFC' : '2px solid transparent', background: t.dark || '#475569',
        color: '#FFFFFF', fontSize: 15, fontWeight: 800, display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 4, opacity: disabled ? 0.45 : on ? 1 : 0.92, boxShadow: on ? '0 0 0 3px rgba(59,130,246,.55)' : 'none',
      };
    }
    return {
      height: 64, borderRadius: 16, border: on ? `2px solid ${C.orange}` : `1px solid ${C.border}`, background: on ? C.orangeTint : '#FFFFFF',
      color: C.ink, fontSize: 15, fontWeight: 800, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', gap: 4, opacity: disabled ? 0.45 : 1,
    };
  };
  const chip = (on) => ({
    height: 40, padding: '0 14px', borderRadius: 999, border: `1px solid ${on ? P.chipOn : P.border}`,
    background: on ? P.chipOn : 'transparent', color: on ? P.chipOnFg : P.text, fontSize: 13.5, fontWeight: 700,
  });
  const small = { height: 48, minWidth: 64, padding: '0 14px', borderRadius: 12, border: `1px solid ${P.border}`, background: P.card, color: P.text, fontSize: 14, fontWeight: 700 };
  const noteStyle = note && {
    ok: { background: P.okBg, color: P.ok }, warn: { background: P.warnBg, color: P.warn }, bad: { background: P.badBg, color: P.bad },
  }[note.tone];
  const verb = tender === 'card' ? `Charge ${usd(amount + tip)} on card`
    : tender === 'cash' ? `Take ${usd(amount)} cash`
      : tender ? `Record ${usd(amount)} by ${TENDER_LABEL[tender] || tender}` : 'Choose how the customer pays';

  return (
    <div data-testid="split-flow" style={{ display: 'flex', flexDirection: 'column', gap: 14, color: P.text }}>
      {/* REMAINING - what the next payment is asked for */}
      <div data-testid="split-remaining-panel" style={{ borderRadius: 18, background: view.complete ? P.okBg : P.panel, padding: '14px 18px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: view.complete ? P.ok : P.panelInk }}>{view.complete ? 'Paid in full' : 'Remaining'}</span>
          <span data-testid="split-remaining" className="c29-tight" style={{ fontFamily: FONT_TIGHT, fontSize: 44, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1.05, color: P.big }}>{usd(view.due)}</span>
        </div>
        <div style={{ textAlign: 'right', fontSize: 14, lineHeight: 1.6, color: P.muted }}>
          <div>Total <b data-testid="split-total" style={{ color: P.text }}>{usd(view.base)}</b></div>
          <div>Paid <b data-testid="split-paid" style={{ color: P.ok }}>{usd(view.paid)}</b></div>
        </div>
      </div>

      {view.payments.length > 0 && (
        <div data-testid="split-paid-list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {view.payments.map((p, i) => (
            <div key={p.key} data-testid={`split-paid-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 12, padding: '9px 12px', background: p.voided ? 'transparent' : P.okBg, border: p.voided ? `1px dashed ${P.border}` : 'none' }}>
              <span style={{ width: 22, height: 22, borderRadius: '50%', background: p.voided ? P.border : C.green, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                {!p.voided && <IconCheck size={12} sw={3.2} />}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 700, textDecoration: p.voided ? 'line-through' : 'none', color: p.voided ? P.muted : P.text }}>
                {p.label}
                {p.tip > 0 && <span style={{ fontWeight: 500, fontSize: 12.5, color: P.muted }}> · incl. {usd(p.tip)} tip</span>}
                {p.fee > 0 && <span style={{ fontWeight: 500, fontSize: 12.5, color: P.muted }}> · fee {usd(p.fee)}</span>}
                {p.voided && <span style={{ fontWeight: 500, fontSize: 12.5 }}> · voided</span>}
              </span>
              <span className="c29-tight" style={{ fontSize: 16, fontWeight: 800, color: p.voided ? P.muted : P.ok }}>{usd(p.amount + p.tip)}</span>
            </div>
          ))}
        </div>
      )}

      {note && <div role="status" data-testid="split-note" data-tone={note.tone} style={{ ...noteStyle, borderRadius: 12, padding: '10px 12px', fontSize: 14, fontWeight: 650, lineHeight: 1.45 }}>{note.text}</div>}

      {waiting && (
        <div data-testid="split-waiting" style={{ borderRadius: 16, border: `1px solid ${P.border}`, padding: 16, display: 'flex', alignItems: 'center', gap: 14, background: P.warnBg }}>
          <span style={{ color: P.warn, flex: 'none' }}><IconSpinner size={28} /></span>
          <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 16, fontWeight: 800 }}>Insert, tap or swipe the card{waiting.amount ? ` · ${usd(waiting.amount)}` : ''}</span>
            <span style={{ fontSize: 13, color: P.muted }}>On {terminalLabel || 'the card terminal'} - waiting for Aster Gateway's answer.</span>
          </span>
          <button type="button" onClick={cancelCard} disabled={busy} data-testid="split-waiting-cancel" style={{ ...small, color: P.danger }}>Cancel</button>
        </div>
      )}

      {!waiting && unknown && !view.complete && (
        <div data-testid="split-unknown" style={{ borderRadius: 16, padding: 14, background: P.warnBg, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontSize: 14, fontWeight: 650, color: P.warn, lineHeight: 1.45 }}>The terminal did not answer for the card of {usd(unknown.amount)}. Check again asks Aster Gateway with the same request - the card is never charged twice.</span>
          <button type="button" onClick={checkAgain} disabled={busy || !cardReady} data-testid="split-check-again" className="c29-press"
            style={{ height: 56, borderRadius: 14, border: 'none', background: P.accent, color: P.accentFg, fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {busy ? <IconSpinner size={18} /> : <IconRefresh size={18} />} Check again · {usd(unknown.amount)}
          </button>
        </div>
      )}

      {!waiting && !unknown && !view.complete && view.due > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {[['custom', 'Custom amount'], ['even', 'Even split'], ...(view.lines.length ? [['item', 'By item']] : [])].map(([h, label]) => (
              <button key={h} type="button" onClick={() => pickHelper(h)} aria-pressed={helper === h} data-testid={`split-helper-${h}`} className="c29-press" style={chip(helper === h)}>{label}</button>
            ))}
          </div>

          {helper === 'even' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="button" aria-label="Fewer ways" disabled={ways <= EVEN_MIN} onClick={() => setWays((w) => Math.max(EVEN_MIN, w - 1))} data-testid="split-even-minus" style={{ ...small, minWidth: 48, opacity: ways <= EVEN_MIN ? 0.45 : 1 }}><IconMinus size={18} /></button>
              <span data-testid="split-even-ways" className="c29-tight" style={{ fontSize: 26, fontWeight: 800, width: 36, textAlign: 'center' }}>{ways}</span>
              <button type="button" aria-label="More ways" disabled={ways >= EVEN_MAX} onClick={() => setWays((w) => Math.min(EVEN_MAX, w + 1))} data-testid="split-even-plus" style={{ ...small, minWidth: 48, opacity: ways >= EVEN_MAX ? 0.45 : 1 }}><IconPlus size={18} /></button>
              <span data-testid="split-even-label" style={{ fontSize: 15, fontWeight: 700, color: P.muted }}>ways · Part {evenPart} of {ways} · {usd(evenAmount(view.due, ways, taken - evenFrom))}</span>
            </div>
          )}

          {helper === 'item' && (
            <div data-testid="split-items" style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
              {view.lines.map((l) => {
                const paid = view.paidItems.has(l.id);
                const on = chosen.has(l.id);
                return (
                  <button key={l.id} type="button" disabled={paid} onClick={() => toggleItem(l.id)} data-testid={`split-item-${l.id}`} aria-pressed={on}
                    style={{ minHeight: 46, borderRadius: 12, border: on ? `2px solid ${dark ? '#3B82F6' : C.orange}` : `1px solid ${P.border}`, background: on ? P.panel : 'transparent', color: P.text, display: 'flex', alignItems: 'center', gap: 10, padding: '0 12px', textAlign: 'left', opacity: paid ? 0.6 : 1 }}>
                    <span style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${on || paid ? (paid ? C.green : dark ? '#3B82F6' : C.orange) : P.faint}`, background: paid ? C.green : on ? (dark ? '#3B82F6' : C.orange) : 'transparent', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                      {(on || paid) && <IconCheck size={11} sw={3.4} />}
                    </span>
                    <span style={{ flex: 1, fontSize: 14.5, fontWeight: 650 }}>{l.qty > 1 ? `${l.qty}× ` : ''}{l.name}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: paid ? P.ok : P.muted }}>{paid ? 'Paid' : usd(l.amount)}</span>
                  </button>
                );
              })}
              <span style={{ fontSize: 12.5, color: P.muted }}>The amount includes the items' share of tax.</span>
            </div>
          )}

          <span data-testid="split-next-label" style={{ fontSize: 15, fontWeight: 800 }}>
            {firstPayment ? 'First payment' : 'Next payment'} <span style={{ fontWeight: 600, color: P.muted }}>— choose how the customer pays</span>
          </span>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(Math.max(tenderList.length, 2), 4)}, minmax(0, 1fr))`, gap: 8 }}>
            {tenderList.map((t) => {
              const Icon = TILE[t.id]?.icon;
              return (
                <button key={t.id} type="button" className="c29-press" onClick={() => { setTender(t.id); setNote(null); }} disabled={busy}
                  aria-pressed={tender === t.id} data-testid={`split-tender-${t.id}`} style={tileStyle(t.id, tender === t.id, busy)}>
                  {Icon ? <Icon size={20} /> : null}
                  {t.label || TENDER_LABEL[t.id] || t.id}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <label style={{ flex: 1, position: 'relative', display: 'flex' }}>
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 20, fontWeight: 700, color: P.muted }}>$</span>
              <input inputMode="decimal" aria-label="Amount" value={amountText} disabled={busy} data-testid="split-amount"
                onChange={(e) => { setAmountText(e.target.value.replace(/[^0-9.]/g, '').slice(0, 9)); if (helper === 'even') setHelper('custom'); }}
                onFocus={(e) => e.target.select()}
                style={{ width: '100%', height: 56, borderRadius: 14, border: `1px solid ${P.border}`, background: P.input, color: P.text, fontSize: 24, fontWeight: 800, padding: '0 14px 0 32px', fontFamily: FONT_TIGHT }} />
            </label>
            <button type="button" disabled={busy} onClick={() => { setHelper('custom'); setAmountText(asText(Math.round(view.due / 2))); }} data-testid="split-half" style={small}>Half</button>
            <button type="button" disabled={busy} onClick={() => { setHelper('custom'); setAmountText(asText(view.due)); }} data-testid="split-all" style={small}>All</button>
          </div>
          {amount > 0 && amount < view.due && <span data-testid="split-after" style={{ fontSize: 13, color: P.muted, marginTop: -6 }}>{usd(view.due - amount)} will remain for the next payment.</span>}

          {tender === 'card' && tips.enabled && (tips.onTerminal ? (
            <span style={{ fontSize: 13, color: P.muted }}>The customer adds a tip on the terminal.</span>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[0, ...(tips.presets || [])].slice(0, 5).map((pct) => (
                <button key={pct} type="button" aria-pressed={tipPct === pct} onClick={() => setTipPct(pct)} data-testid={`split-tip-${pct}`} className="c29-press" style={chip(tipPct === pct)}>
                  {pct ? `${pct}% · ${usd(Math.round((amount * pct) / 100))}` : 'No tip'}
                </button>
              ))}
            </div>
          ))}

          {tender === 'cash' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: P.muted, whiteSpace: 'nowrap' }}>Cash received</span>
              <input inputMode="decimal" value={tendered} placeholder={asText(amount)} data-testid="split-tendered" disabled={busy}
                onChange={(e) => setTendered(e.target.value.replace(/[^0-9.]/g, '').slice(0, 9))}
                style={{ flex: 1, height: 48, borderRadius: 12, border: `1px solid ${P.border}`, background: P.input, color: P.text, fontSize: 18, fontWeight: 700, padding: '0 12px' }} />
              {cashIn !== null && cashIn > amount && <span data-testid="split-change" style={{ fontSize: 14, fontWeight: 800, color: P.warn }}>Change {usd(cashIn - amount)}</span>}
            </label>
          )}

          {tender === 'card' && !cardReady && <span style={{ fontSize: 13, color: P.bad }}>{cardHint || 'Choose this register’s card terminal first'}</span>}

          <button type="button" onClick={process} disabled={busy || !tender || !!problem} data-testid="split-process" className="c29-press"
            style={{ height: 60, borderRadius: 16, border: 'none', background: P.accent, color: P.accentFg, fontSize: 18, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, opacity: busy || !tender || problem ? 0.55 : 1 }}>
            {busy ? <IconSpinner size={20} /> : tender === 'card' ? <IconCard size={22} /> : tender === 'cash' ? <IconCash size={22} /> : <IconSplit size={20} />}
            {busy ? 'Processing…' : verb}
          </button>
          {tender && problem && amountText !== '' && <span data-testid="split-problem" style={{ fontSize: 13, color: P.bad, marginTop: -6 }}>{problem}</span>}
        </>
      )}

      {!view.complete && !waiting && (
        <button type="button" onClick={cancelSplit} disabled={busy} data-testid="split-cancel"
          style={{ alignSelf: 'center', border: 'none', background: 'transparent', color: P.link, fontSize: 14, fontWeight: 700, minHeight: 40, textDecoration: 'underline' }}>
          {taken > 0 ? 'Stop here · leave the balance due' : 'Cancel split'}
        </button>
      )}
    </div>
  );
});

export default SplitPayFlow;

// ------------------------------------------------------------------ normalizers

const cardLabel = (brand, last4) => `${brand && brand.toLowerCase() !== 'card' ? `${brand[0].toUpperCase()}${brand.slice(1).toLowerCase()} ` : 'Card '}${last4 ? `•••• ${last4}` : ''}`.trim();

function common(split) {
  const parts = split?.parts || [];
  const paidItems = new Set(parts.filter((p) => p.covered_cents > 0 && p.due_cents === 0).flatMap((p) => p.items || []));
  const unknownPart = parts.find((p) => p.status === 'unknown' && !p.open_payment_id);
  let unknown = null;
  if (unknownPart) {
    const t = [...(unknownPart.tries || [])].reverse().find((x) => x.payment_id && x.result_unknown);
    unknown = { n: unknownPart.n, amount: t ? t.amount_cents + (t.tip_cents || 0) : unknownPart.due_cents };
  }
  return { paidItems, unknownPart: unknown, waitingPaymentId: split?.waiting_payment_id || null };
}

/** The register's answer (GET /api/checkout/orders/{id}/split). */
export function registerView(a) {
  if (!a) return null;
  return {
    due: a.due_cents ?? 0, base: a.base_cents ?? 0, paid: a.paid_cents ?? 0, split: a.split,
    payments: (a.tenders || []).map((t, i) => ({
      key: `${t.kind}-${t.payment_id || t.tender_id || i}`,
      label: t.kind === 'card' ? cardLabel(t.card_brand, t.last4) : (TENDER_LABEL[t.kind] || t.kind),
      amount: t.amount_cents || 0, tip: t.tip_cents || 0, fee: t.fee_cents || 0, voided: !!t.voided,
    })),
    lines: (a.lines || []).map((l) => ({ id: l.order_item_id, name: l.name, qty: l.quantity, amount: l.amount_cents })),
    complete: a.payment_status === 'paid',
    ...common(a.split),
  };
}

/** A dining check (check_view). */
export function checkView(c) {
  if (!c) return null;
  const base = Math.max((c.total_cents || 0) - (c.tip_cents || 0), 0);
  const due = c.balance_due_cents ?? Math.max((c.due_cents || 0) - (c.tip_cents || 0), 0);
  const cards = (c.card_payments || []).filter((p) => p.status === 'approved').map((p) => ({
    key: `card-${p.payment_id}`, label: cardLabel(p.card_brand, p.last4),
    amount: p.approved_cents ?? p.amount_cents, tip: p.tip_cents || 0, fee: 0, voided: !!p.voided,
  }));
  const others = (c.tenders || []).map((t) => ({
    key: `t-${t.id}`, label: TENDER_LABEL[t.method] || t.method || 'Cash', amount: t.applied_cents || 0, tip: t.tip_cents || 0, fee: 0, voided: false,
  }));
  return {
    due, base, paid: Math.max(base - due, 0), split: c.split, payments: [...cards, ...others],
    lines: (c.lines || []).filter((l) => !l.voided && l.amount_cents > 0).map((l) => ({ id: l.order_item_id, name: l.name, qty: l.quantity, amount: l.amount_cents })),
    complete: c.status === 'paid',
    ...common(c.split),
  };
}
